import React from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { CustomMarker, isTextResult } from '../../models/CustomMarker';
import {
  Lab,
  LabPanel,
  LAB_PANELS,
  panelSectionTitle,
  panelsByLab,
  shippedSectionLabel,
} from '../../models/LabPanelCatalog';
import './lab-panel-picker.css';

/**
 * "Add a lab panel/marker" on My Markers: pick a test a lab runs as one order
 * and take its markers, units and reference ranges in one go.
 *
 * Panel/marker, not panel, because a third of the catalog is a test that
 * reports ONE marker — Cardio IQ TMAO, MPO, OxLDL, Lp-PLA2 Activity, the
 * Insulin Resistance Score. Each is still a single order, which is what this
 * list is organized by, so the noun in the UI is "test" and the button names
 * both of the things a practitioner might be here to add.
 *
 * Its own component rather than a tenth branch of CustomMarkerLibrary, which is
 * already the longest file on this page, and because nothing here touches a
 * marker row: the picker decides WHAT to add and hands it back.
 *
 * Three decisions worth keeping:
 *
 * 1. **The lab is named on every row, and again over the group.** Every panel is
 *    lab-scoped (see LabPanelCatalog: the same analyte differs by lab in name
 *    AND range) and only ever fills from the lab whose spelling it carries, so
 *    which lab a panel belongs to has to be readable where the practitioner is
 *    looking, not only in a heading several rows above. It is NOT folded into
 *    the title, because the title is also the default section name and that is
 *    read by the client on their report, to whom "(Quest)" is noise. No
 *    section name carries a lab as of 2026-10-13; `panelSectionTitle` has the
 *    measurement that ended it.
 * 2. **The marker list is shown before anything is added.** This writes up to
 *    fourteen rows into a page the practitioner then has to save, and the names
 *    are the only way to tell a mold IgG panel from a mold IgE one.
 * 3. **What they already have is counted, not re-added.** A marker name is
 *    unique across the whole library, so a second copy is not something they
 *    could keep even if they wanted it.
 *
 * Deliberately NOT a large modal. Every line in here is short — a panel title,
 * a marker name, a count — so at `size="lg"` the right half of every row was
 * empty and the list read as a table with a column missing. The default width
 * is what the content actually needs, and each row lays its title, lab, count
 * and status out in one flex line rather than leaving a gap between them.
 */

type Props = {
  show: boolean;
  onClose: () => void;
  /**
   * Every marker name on the page, saved or not. Drives both the "already have
   * it" count and what Add would actually write.
   */
  takenNames: string[];
  /**
   * Where each of those already sits, keyed by `CustomMarker.keyFor`.
   *
   * A panel LEAVES a marker it already has exactly where it is, so a
   * practitioner adding the Labcorp urinalysis with twelve of its rows already
   * filed under a Hospital Joya one gets a new section holding six, and
   * nothing on the screen says where the other twelve went. Naming the section
   * is the whole answer, and it is the same answer the library's
   * duplicate-name error already gives.
   *
   * Optional, because the count and the add both work without it; absent, the
   * line falls back to the bare "already in My Markers".
   */
  takenSections?: { [nameKey: string]: string };
  /** The practitioner's own section names, offered beside the section field. */
  sectionNames: string[];
  onAdd: (panel: LabPanel, sectionName: string) => void;
  /** Panels to offer. Defaults to the shipped catalog; set by tests. */
  panels?: LabPanel[];
};

type State = {
  selectedId?: string;
  /** Edited section name, kept per panel so switching panels resets it. */
  sectionName: string;
  search: string;
  /**
   * Which labs the practitioner has opened. Closed is the default: they know
   * which lab they drew to, and the list they want is one click away, while the
   * list they do not want is not in their way at all.
   */
  openLabs: { [lab: string]: boolean };
};

/** How a panel's markers split against what the library already holds. */
export function panelStanding(
  panel: LabPanel,
  takenNames: string[],
): { missing: string[]; had: string[] } {
  const taken = new Set(
    (takenNames || []).map(name => CustomMarker.keyFor(name || '')).filter(Boolean),
  );
  const missing: string[] = [];
  const had: string[] = [];
  (panel.markers || []).forEach(marker => {
    (taken.has(CustomMarker.keyFor(marker.name)) ? had : missing).push(
      marker.name,
    );
  });
  return { missing, had };
}

/** The Add button's label, which is also the clearest statement of what it does. */
export function addButtonLabel(missing: number, had: number): string {
  if (!missing) {
    return 'Nothing to add';
  }
  if (had) {
    return `Add ${missing} missing marker${missing === 1 ? '' : 's'}`;
  }
  return `Add ${missing} marker${missing === 1 ? '' : 's'}`;
}

/**
 * How a panel's codes read on the panel a practitioner has opened, in the
 * lab's own words: Labcorp's menu calls the number a "Test Number" and Quest's
 * calls it a "Test Code", and neither of them calls it an order.
 *
 * Empty for a panel with no code, which is every one Labcorp's compendium does
 * not carry and both of Quest's.
 */
export function testCodeLine(panel: LabPanel): string {
  const codes = panel.testCodes || [];
  if (!codes.length) {
    return '';
  }
  const word = panel.lab === Lab.LABCORP ? 'test number' : 'test code';
  const shown = codes.slice(0, 3).join(', ');
  const rest = codes.length > 3 ? ` and ${codes.length - 3} more` : '';
  return `${panel.lab} ${word}${codes.length === 1 ? '' : 's'} ${shown}${rest}`;
}

const SUBTLE: React.CSSProperties = { fontSize: '12px', color: '#6c757d' };

export class LabPanelPicker extends React.Component<Props, State> {
  state: State = { sectionName: '', search: '', openLabs: {} };

  private get panels(): LabPanel[] {
    return this.props.panels || LAB_PANELS;
  }

  private get selected(): LabPanel | undefined {
    return this.panels.find(panel => panel.id === this.state.selectedId);
  }

  /**
   * Open a panel, or close the one already open.
   *
   * A SECOND CLICK CLOSES IT. The row is a disclosure, and a disclosure that
   * only ever opens is a trap: before this, the single way to put a panel away
   * was to open a DIFFERENT one, so a practitioner who opened the fourteen-row
   * mold panel to read it had no way back to a short list.
   *
   * Closing clears the edited section name with it, for the same reason
   * `close` does: reopening on a half-typed name from a minute ago is a
   * surprise, and the name is one keystroke to get back.
   */
  private select = (panel: LabPanel) => {
    this.setState(state =>
      state.selectedId === panel.id
        ? { selectedId: undefined, sectionName: '' }
        : { selectedId: panel.id, sectionName: panelSectionTitle(panel) },
    );
  };

  private close = () => {
    // Cleared on the way out: reopening on a half-edited section name from last
    // time would be a surprise, and the panel list is cheap to re-read.
    this.setState({
      selectedId: undefined,
      sectionName: '',
      search: '',
      openLabs: {},
    });
    this.props.onClose();
  };

  private matches(panel: LabPanel): boolean {
    const search = this.state.search.trim().toLowerCase();
    if (!search) {
      return true;
    }
    // Searched by what a practitioner would type: the test's name, the lab, a
    // test code off the requisition, or a marker they know is in it. The code
    // is still searched although the rows no longer print it: a practitioner
    // holding a requisition has the number in front of them.
    return [
      panel.title,
      panel.lab,
      panelSectionTitle(panel),
      ...panel.testCodes,
      ...panel.markers.map(marker => marker.name),
    ].some(text => String(text).toLowerCase().includes(search));
  }

  /**
   * One lab's panels, behind a bar that opens them.
   *
   * Closed by default. With more than one lab in the catalog the list a
   * practitioner wants is the one lab they drew to, and every other panel in
   * front of it is something to scroll past; a named bar is also the plainest
   * possible statement of which lab a panel belongs to, which matters because a
   * panel only ever fills from the lab whose spelling it carries.
   *
   * A SEARCH opens every group it matched, because a search that appears to
   * return nothing is worse than no search at all.
   */
  private renderLabGroup(
    group: { lab: Lab; panels: LabPanel[] },
    searching: boolean,
  ) {
    const markerCount = (group.panels || []).reduce(
      (total, panel) => total + (panel.markers || []).length,
      0,
    );
    const open = searching || !!this.state.openLabs[group.lab];
    return (
      <div key={group.lab}>
        <button
          type="button"
          className="lab-panel-lab"
          aria-expanded={open}
          onClick={() =>
            this.setState(state => ({
              openLabs: { ...state.openLabs, [group.lab]: !open },
            }))
          }
          style={{
            display: 'block',
            width: '100%',
            textAlign: 'left',
            border: 'none',
            /* The modal header's own blue. Smaller and lighter than the title
               above it on purpose: two bars in the same blue at the same weight
               read as two headers, and only one of them is the dialog. */
            background: '#337ab7',
            color: '#fff',
            fontSize: '13px',
            padding: '5px 10px',
          }}
        >
          <FontAwesomeIcon
            icon={open ? 'chevron-down' : 'chevron-right'}
            style={{ fontSize: '10px' }}
          />
          &nbsp;&nbsp;
          <span className="lab-panel-lab-name">{group.lab}</span>
          {/*
            * On the bar, so a closed group still says what is behind it.
            *
            * MARKERS, NOT A COUNT OF THE ENTRIES. It counted entries until
            * 2026-10-13 and called them "tests", which was accurate and not
            * useful: the number a practitioner wants before opening a group is
            * how much it would save them typing, and the count of rows they
            * are about to scroll past answers a question nobody asked.
            *
            * It also retires a word that kept being wrong in one direction or
            * the other. "Panels" overstates, because 13 of the 29 entries
            * report a single marker, and on the Quest bar that is 10 of 15.
            * "Tests" was true of all 29 and read as a technicality. Markers are
            * the thing being offered, so counting them needs no collective noun
            * for the containers at all.
            *
            * It follows the filter, like the groups themselves: searching
            * narrows the bar to the markers in the tests that matched.
            */}
          <span style={{ fontSize: '12px', opacity: 0.85, marginLeft: '8px' }}>
            {markerCount} marker{markerCount === 1 ? '' : 's'}
          </span>
        </button>
        {open
          ? group.panels.map(panel => (
              <React.Fragment key={panel.id}>
                {this.renderPanelRow(panel)}
                {/* Under the panel it describes, not under the whole list. A
                    detail block at the bottom of the dialog was read as
                    belonging to the LAST lab rather than to the selected row,
                    which is exactly the thing this picker must not get wrong. */}
                {this.state.selectedId === panel.id
                  ? this.renderSelected(panel)
                  : null}
              </React.Fragment>
            ))
          : null}
      </div>
    );
  }

  private renderPanelRow(panel: LabPanel) {
    const { takenNames } = this.props;
    const { missing, had } = panelStanding(panel, takenNames);
    const selected = this.state.selectedId === panel.id;
    const complete = !missing.length;
    return (
      <button
        key={panel.id}
        type="button"
        className="lab-panel-row"
        /* A disclosure, not a toggle button: it opens and closes the block
           under it, which is what `aria-expanded` means and what the lab bar
           above it already says. */
        aria-expanded={selected}
        onClick={() => this.select(panel)}
        style={{
          display: 'block',
          width: '100%',
          textAlign: 'left',
          border: 'none',
          borderBottom: '1px solid #e9ecef',
          background: selected ? '#eef4fa' : 'transparent',
          padding: '8px 10px',
        }}
      >
        <span
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: '8px',
            flexWrap: 'wrap',
          }}
        >
          {/* The same chevron the lab bar above carries, so the row says it
              opens and closes before it is clicked rather than after. Muted
              and a size down: the bar is the louder control of the two, and a
              row of black chevrons competes with the panel names, which are
              the thing being scanned. `flexWrap` is on this line, so the icon
              gets a fixed width to keep every title on one left edge whichever
              way the chevron points. */}
          <FontAwesomeIcon
            icon={selected ? 'chevron-down' : 'chevron-right'}
            style={{
              fontSize: '9px',
              color: '#868e96',
              width: '9px',
              flex: '0 0 auto',
            }}
          />
          <span
            className="lab-panel-row-title"
            style={{ fontSize: '14px', color: '#212529' }}
          >
            {panel.title}
          </span>
        {/* The lab on the ROW, not only on the heading above it. A practitioner
            picks a panel by the lab they draw to, and a heading several rows up
            is not where they are looking when they read the title. Repeating it
            is cheap; picking a Quest panel for a Labcorp report is not, because
            a panel only ever fills from the lab whose spelling it carries. */}
          <span
            className="lab-panel-row-lab"
            style={{
              fontSize: '11px',
              color: '#495057',
              background: '#eef1f4',
              borderRadius: '3px',
              padding: '1px 5px',
              whiteSpace: 'nowrap',
            }}
          >
            {panel.lab}
          </span>
          <span style={SUBTLE}>
            {panel.markers.length} marker{panel.markers.length === 1 ? '' : 's'}
          </span>
          {complete ? (
            <span style={{ ...SUBTLE, color: '#2f7a3f' }}>already added</span>
          ) : null}
          {!complete && had.length ? (
            <span style={SUBTLE}>
              {had.length} of {panel.markers.length} already added
            </span>
          ) : null}
        </span>
        {/* No test code here. It is still searchable, and it is printed on the
            panel once opened, but a number under every title is noise against
            the one thing this list is for: finding a test by its name. */}
      </button>
    );
  }

  private renderSelected(panel: LabPanel) {
    const { missing, had } = panelStanding(panel, this.props.takenNames);
    const taken = new Set(
      (this.props.takenNames || [])
        .map(name => CustomMarker.keyFor(name || ''))
        .filter(Boolean),
    );
    return (
      /* Same background as the selected row above it, no gap, and inset from
         the left, so the two read as one block belonging to that panel. */
      <div
        className="lab-panel-detail"
        style={{
          background: '#eef4fa',
          borderBottom: '1px solid #e9ecef',
          padding: '0 10px 12px 22px',
        }}
      >
        {/* No title line: the row directly above carries the panel's name and
            its lab, and repeating them here was the only thing that made this
            look like a section of its own. */}
        {panel.note ? <div style={SUBTLE}>{panel.note}</div> : null}
        {/* The code belongs on the panel they are considering, where it answers
            "is this the one I ordered?", rather than under every title in the
            list, where it was a column of numbers to read past. */}
        {testCodeLine(panel) ? (
          <div style={SUBTLE}>{testCodeLine(panel)}</div>
        ) : null}

        {/* A SHIPPED SECTION IS STATED, NOT OFFERED. These markers print
            inside an order whose other rows the app already charts, and the
            section they join is one of the report's own, which the
            practitioner cannot rename from here. An editable box would be
            promising a choice that the field behind it does not have: the row
            carries a `Section`, not a name. */}
        {panel.section ? (
          <div style={{ fontSize: '13px', color: '#212529', marginTop: '10px' }}>
            Added to{' '}
            <strong>{shippedSectionLabel(panel.section)}</strong>, beside the
            rest of this order.
          </div>
        ) : (
          <Form.Group style={{ marginTop: '10px', marginBottom: '6px' }}>
            <Form.Label style={{ fontSize: '13px', marginBottom: '2px' }}>
              Add to section
            </Form.Label>
            <Form.Control
              type="text"
              list="lab-panel-section-names"
              className="custom-marker-input"
              style={{ fontSize: '13px', height: '31px', maxWidth: '320px' }}
              value={this.state.sectionName}
              onChange={(event: any) =>
                this.setState({ sectionName: event.target.value })
              }
            />
            {/* The practitioner's own section names, so a panel can join a
                section they already read rather than making a near-duplicate. */}
            <datalist id="lab-panel-section-names">
              {(this.props.sectionNames || []).map(name => (
                <option key={name} value={name} />
              ))}
            </datalist>
            <div style={SUBTLE}>
              A section with this name is created if you do not have one yet.
            </div>
          </Form.Group>
        )}

        <div style={{ fontSize: '13px', color: '#212529', marginTop: '10px' }}>
          {panel.markers.length} marker{panel.markers.length === 1 ? '' : 's'}
        </div>
        <ul
          className="lab-panel-markers"
          style={{
            fontSize: '13px',
            margin: '4px 0 0',
            paddingLeft: '18px',
          }}
        >
          {panel.markers.map(marker => {
            const already = taken.has(CustomMarker.keyFor(marker.name));
            // Where it already is, when the caller passed the sections. The
            // section a panel would have filed it under is NOT where it is:
            // a marker already in the library is left exactly where it sits.
            const where = already
              ? (this.props.takenSections || {})[CustomMarker.keyFor(marker.name)]
              : undefined;
            return (
              <li
                key={marker.name}
                style={{ color: already ? '#868e96' : '#212529' }}
              >
                {marker.name}
                {marker.units ? (
                  <span style={SUBTLE}> {marker.units}</span>
                ) : null}
                {isTextResult(marker) ? (
                  <span style={SUBTLE}> reported in words</span>
                ) : null}
                {already ? (
                  <span style={SUBTLE}>
                    {where ? ` already in ${where}` : ' already in My Markers'}
                  </span>
                ) : null}
              </li>
            );
          })}
        </ul>
        {!missing.length ? (
          <div style={{ ...SUBTLE, marginTop: '8px' }}>
            Every marker in this test is already in My Markers, so there is
            nothing to add.
          </div>
        ) : (
          <div style={{ ...SUBTLE, marginTop: '8px' }}>
            {had.length
              ? `${had.length} you already have ${had.length === 1 ? 'is' : 'are'} left exactly as ${had.length === 1 ? 'it is' : 'they are'}. `
              : ''}
            Nothing is written until you save this page.
          </div>
        )}
      </div>
    );
  }

  render() {
    const { show } = this.props;
    if (!show) {
      return null;
    }
    const searching = !!this.state.search.trim();
    const groups = panelsByLab(this.panels.filter(panel => this.matches(panel)));
    const panel = this.selected;
    const standing = panel
      ? panelStanding(panel, this.props.takenNames)
      : { missing: [], had: [] };

    return (
      /* `scrollable` keeps the dialog inside the viewport however long a panel
         is: the body scrolls and the footer, which carries the Add button,
         stays where it can be reached. A fourteen-marker panel ran off the
         bottom of the screen without it. */
      <Modal show onHide={this.close} scrollable>
        <Modal.Header
          closeButton
          style={{ backgroundColor: '#337ab7', color: '#fff' }}
        >
          <Modal.Title style={{ fontSize: '18px' }}>
            Add a lab panel/marker
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: '14px' }}>
          <p style={{ fontSize: '13px', color: '#212529' }}>
            Tests a lab runs as a single order, with that lab&rsquo;s own units
            and reference ranges. Some are a panel of markers, and some are a
            single marker. The lab matters: the same marker is reported under a
            different name, and against a different range, from one lab to the
            next.
          </p>
          <Form.Control
            type="text"
            className="custom-marker-input"
            style={{
              fontSize: '13px',
              height: '31px',
              maxWidth: '260px',
              marginBottom: '10px',
            }}
            placeholder="Search by test, marker or test code"
            value={this.state.search}
            onChange={(event: any) =>
              this.setState({ search: event.target.value })
            }
          />
          {groups.length === 0 ? (
            <div style={SUBTLE}>No test matches that search.</div>
          ) : (
            /* No inner scroller. The modal body is the only thing that
               scrolls, which is what closing the groups by default buys: a
               list inside a scrolling dialog gave two scrollbars a few pixels
               apart, and the inner one hid the rows it was meant to reveal. */
            <div style={{ border: '1px solid #e9ecef' }}>
              {groups.map(group => this.renderLabGroup(group, searching))}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="link" style={{ color: '#6c757d' }} onClick={this.close}>
            Cancel
          </Button>
          {/* Solid blue, not an outline: it is the dialog's one committing
              action, and the outline read as a second cancel beside it. */}
          <Button
            variant="primary"
            disabled={!panel || !standing.missing.length}
            onClick={() => {
              if (!panel) {
                return;
              }
              this.props.onAdd(panel, this.state.sectionName.trim());
              this.close();
            }}
          >
            {panel
              ? addButtonLabel(standing.missing.length, standing.had.length)
              : 'Add a panel/marker'}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}
